import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import {
  Assignment,
  History,
  Medication,
  Bloodtype,
  Pets,
  Restaurant,
  Vaccines,
  BugReport,
  MonitorHeart,
} from "@mui/icons-material";
import { Controller } from "react-hook-form";
import { HistoryModal } from "./HistoryModal";
import { useVetAttendance } from "@/fluxvet/contexts/VetAttendanceContext";
import {
  extractMainComplaintHistory,
  extractMedicationHistory,
  extractExamsHistory,
  extractStreetAccessHistory,
  extractFeedingHistory,
  extractVaccinationHistory,
  extractEctoparasiteHistory,
  extractWeightHistory,
  extractDewormingHistory,
} from "@/fluxvet/utils/questionnaireResponseHelper";

// =============================================================================
// TIPOS - Interfaces
// =============================================================================

/**
 * Props do componente AnamneseSection
 */
interface AnamnesesSectionProps {
  control: any;
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Componente de Anamnese e Histórico Clínico (FUSÃO)
 * Exibe formulário estruturado combinando informações iniciais e histórico clínico
 * Inclui botões para visualizar histórico de cada campo
 *
 * Seções incluídas:
 *
 * ANAMNESE:
 * - Queixa Principal (obrigatório)
 * - Vermifugação
 * - Peso
 * - Vacinação
 *
 * HISTÓRICO CLÍNICO:
 * - Uso de medicação contínua
 * - Exames recentes
 * - Acesso à rua
 * - Alimentação
 * - Vacinação
 * - Controle de Ectoparasitas
 *
 * @param control - Control do react-hook-form
 */
export const AnamneseSection: React.FC<AnamnesesSectionProps> = ({
  control,
}) => {
  // Busca o histórico de QuestionnaireResponse do contexto
  const { historyQuestionnaireResponse } = useVetAttendance();

  // Processa o histórico para extrair dados de cada campo
  // Usa useMemo para evitar reprocessamento desnecessário
  const mainComplaintHistory = useMemo(
    () => extractMainComplaintHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const medicationHistory = useMemo(
    () => extractMedicationHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const examsHistory = useMemo(
    () => extractExamsHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const streetAccessHistory = useMemo(
    () => extractStreetAccessHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const feedingHistory = useMemo(
    () => extractFeedingHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const vaccinationHistory = useMemo(
    () => extractVaccinationHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const ectoparasiteHistory = useMemo(
    () => extractEctoparasiteHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const weightHistory = useMemo(
    () => extractWeightHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const dewormingHistory = useMemo(
    () => extractDewormingHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  // Estado único para controlar o modal de histórico
  const [historyModal, setHistoryModal] = useState<{
    isOpen: boolean;
    title: string;
    history: any[];
  }>({
    isOpen: false,
    title: "",
    history: [],
  });

  /**
   * Função auxiliar para abrir o modal de histórico
   */
  const openHistoryModal = (title: string, history: any[]) => {
    setHistoryModal({ isOpen: true, title, history });
  };

  /**
   * Função auxiliar para fechar o modal de histórico
   */
  const closeHistoryModal = () => {
    setHistoryModal({ isOpen: false, title: "", history: [] });
  };

  return (
    <>
      <Box
        sx={{
          maxHeight: "calc(100vh - 400px)",
          overflowY: "auto",
          pr: 1,
        }}
      >
        <Grid container spacing={2}>
          {/* ========== SEÇÃO: ANAMNESE ========== */}

          {/* Queixa Principal */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Assignment sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Queixa Principal
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Queixa Principal",
                        mainComplaintHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="mainComplaint"
                  control={control}
                  rules={{ required: "Campo obrigatório" }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Descreva a queixa principal"
                      placeholder="Ex: Pet apresenta vômitos há 2 dias..."
                      multiline
                      minRows={4}
                      maxRows={15}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Vermifugação */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <BugReport sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Vermifugação
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Vermifugação",
                        dewormingHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="deworming"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value || ""}
                      onChange={field.onChange}
                      row
                    >
                      {["Sim", "Não"].map((option) => (
                        <FormControlLabel
                          key={option}
                          value={option}
                          sx={{ minWidth: 100 }}
                          control={
                            <Radio
                              size="small"
                              sx={{
                                color: "#00A884",
                                "&.Mui-checked": { color: "#00A884" },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2">{option}</Typography>
                          }
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
                <Controller
                  name="dewormingDescription"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Descrição da vermifugação"
                      placeholder="Descreva o vermífugo aplicado, datas, protocolo..."
                      multiline
                      minRows={3}
                      maxRows={10}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Peso */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <MonitorHeart sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Peso
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal("Histórico - Peso", weightHistory)
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="weight"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Peso"
                      placeholder="Ex: 15.2"
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">kg</InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Vacinação */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Vaccines sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Vacinação
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Vacinação",
                        vaccinationHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />

                {/* Toggle Sim/Não */}
                <Controller
                  name="vaccination"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#00A884",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "#00A884",
                              },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {field.value ? "Sim, vacinado" : "Não vacinado"}
                        </Typography>
                      }
                    />
                  )}
                />

                {/* Campo de descrição condicional */}
                <Controller
                  name="vaccination"
                  control={control}
                  render={({ field: { value: vaccination } }) => (
                    <>
                      {vaccination && (
                        <Controller
                          name="vaccinationDescription"
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              label="Descreva a vacinação"
                              placeholder="Ex: V10 + Antirrábica (20/11/2024), próxima dose em 20/11/2025..."
                              multiline
                              minRows={2}
                              maxRows={8}
                              error={!!error}
                              helperText={error?.message}
                              fullWidth
                              variant="outlined"
                              sx={{
                                "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                              }}
                            />
                          )}
                        />
                      )}
                    </>
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* ========== SEÇÃO: HISTÓRICO CLÍNICO ========== */}

          {/* Uso de medicação contínua */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Medication sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Uso de medicação contínua
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Medicação Contínua",
                        medicationHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="continuousMedication"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Descreva os medicamentos em uso contínuo"
                      placeholder="Ex: Omeprazol 20mg 1x/dia, Prednisona 5mg 2x/dia..."
                      multiline
                      minRows={3}
                      maxRows={10}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Exames recentes */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Bloodtype sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Exames recentes
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Exames Recentes",
                        examsHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="recentExams"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Descreva exames realizados recentemente"
                      placeholder="Ex: Hemograma completo (15/11/2024) - resultados normais..."
                      multiline
                      minRows={3}
                      maxRows={10}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Acesso à rua */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Pets sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Acesso à rua
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Acesso à Rua",
                        streetAccessHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />

                {/* Toggle Sim/Não */}
                <Controller
                  name="streetAccess"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#00A884",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "#00A884",
                              },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {field.value ? "Sim, tem acesso" : "Não tem acesso"}
                        </Typography>
                      }
                    />
                  )}
                />

                {/* Campo de descrição condicional */}
                <Controller
                  name="streetAccess"
                  control={control}
                  render={({ field: { value: streetAccess } }) => (
                    <>
                      {streetAccess && (
                        <Controller
                          name="streetAccessDescription"
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              label="Descreva o acesso à rua"
                              placeholder="Ex: Sai no quintal cercado, passeios com guia 2x/dia..."
                              multiline
                              minRows={2}
                              maxRows={8}
                              error={!!error}
                              helperText={error?.message}
                              fullWidth
                              variant="outlined"
                              sx={{
                                "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                              }}
                            />
                          )}
                        />
                      )}
                    </>
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Alimentação */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Restaurant sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Alimentação
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Alimentação",
                        feedingHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="feeding"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Descreva a alimentação do pet"
                      placeholder="Ex: Ração premium para adultos 200g 2x/dia, petiscos naturais..."
                      multiline
                      minRows={3}
                      maxRows={10}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Controle de Ectoparasitas */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <BugReport sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Controle de Ectoparasitas
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Controle de Ectoparasitas",
                        ectoparasiteHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />

                {/* Toggle Sim/Não */}
                <Controller
                  name="ectoparasiteControl"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#00A884",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "#00A884",
                              },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {field.value ? "Sim, em controle" : "Sem controle"}
                        </Typography>
                      }
                    />
                  )}
                />

                {/* Campo de descrição condicional */}
                <Controller
                  name="ectoparasiteControl"
                  control={control}
                  render={({ field: { value: ectoparasiteControl } }) => (
                    <>
                      {ectoparasiteControl && (
                        <Controller
                          name="ectoparasiteControlDescription"
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              label="Descreva o controle de ectoparasitas"
                              placeholder="Ex: Bravecto 500mg aplicado em 01/12/2024, próxima aplicação 01/03/2025..."
                              multiline
                              minRows={2}
                              maxRows={8}
                              error={!!error}
                              helperText={error?.message}
                              fullWidth
                              variant="outlined"
                              sx={{
                                "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                              }}
                            />
                          )}
                        />
                      )}
                    </>
                  )}
                />
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Modal único de histórico */}
      <HistoryModal
        open={historyModal.isOpen}
        onClose={closeHistoryModal}
        title={historyModal.title}
        history={historyModal.history}
      />
    </>
  );
};

export default AnamneseSection;
